import { ENVIRONMENT_HELPER } from "@/helpers/environment.js";
import store from '@/store.js'
import i18n from '@/helpers/i18n';

export const IS_LOADING_STATUS = "loading";

export const API_HELPER = {
    abortController: new AbortController(),
    pathJoin,
    loggedApiCall,
    apiCall
};

const GENERIC_ERROR = "Something went wrong, please try again. If the problem persists contact the support.";

/* Turns an API error body into a readable message.
   The API answers 400 with { errors: { Field: [msg] }, message } (validation) or { message } (bad request). */
function apiErrorMessage(err)
{
    if(typeof err === 'string' || err instanceof String)
    {
        return err.length ? err : GENERIC_ERROR; // Error responses with an empty body reach here as ""
    }

    var errors = err && (err.errors || err.Errors);
    if(errors && Object.keys(errors).length)
    {
        return Object.keys(errors).map(key => {
            var messages = Array.isArray(errors[key]) ? errors[key].join(" ") : errors[key];
            var field = key.replace(/\[\d+\](\.Value)?$/, ""); // "Tags[0].Value" -> "Tags"
            return `${field}: ${messages}`;
        }).join("\n");
    }

    if(err && (err.message || err.Message) && !(err instanceof Error))
    {
        return err.message || err.Message;
    }

    return GENERIC_ERROR;
}

function pathJoin(parts)
{
    return parts.map((part, i) => 
    {
        if (i === 0) {
          return part.toString().trim().replace(/[\/]*$/g, '')
        } else {
          return part.toString().trim().replace(/(^[\/]*|[\/]*$)/g, '')
        }
    }).filter(x=>x.length).join('/');
}

/* Handles 1) request cancellation 2) refresh the token if the user is not logged in 3) fetch url 4) error handler */
function loggedApiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Validate if the user is logged in.
    let user = JSON.parse(localStorage.getItem('user'));
    if(!user || !user.data.accessToken)
    {
        throw("The user is not logged in or the current session is not valid!");
    }

    // Check if token is expired. Return authorization header with jwt token and compare expired time with the next 10 minutes. The 10 minutes is just a buffer that we allow ourselves to refresh the token.
    var expiredTime = new Date(user.data.accessTokenExpirationDate);

    var limitTime = new Date();
    limitTime.setMinutes(limitTime.getMinutes() + 10);
    var limitTimeUtc = new Date(limitTime.getUTCFullYear(), limitTime.getUTCMonth(), limitTime.getUTCDate(), limitTime.getUTCHours(), limitTime.getUTCMinutes(), limitTime.getUTCSeconds());

    var ensureToken;
    if(expiredTime < limitTimeUtc)
    {
        var refreshUrl = pathJoin([process.env.VUE_APP_API_ROOT, "Users", "refresh"]);
        ensureToken = fetch(encodeURI(refreshUrl), {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userEmail: user.data.email, token: user.data.refreshToken })
        })
        .then(function(resp) {
            if (!resp.ok) return Promise.reject("refresh-failed");
            return resp.json();
        })
        .then(function(refreshed) {
            user.data = Object.assign({}, user.data, refreshed.data);
            localStorage.setItem('user', JSON.stringify(user));
        })
        .catch(function() {
            store.dispatch('users/logout', null, { root: true });
            return Promise.reject("Session expired. Please login again.");
        });
    }
    else
    {
        ensureToken = Promise.resolve();
    }

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }


    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    // Add current language to request
    if(!requestOptions.headers)
    {
        requestOptions.headers = {};
    }
    requestOptions.headers["Accept-Language"] = i18n.locale;
    requestOptions.headers["Authorization"] = "Bearer " + user.data.accessToken;

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return ensureToken.then(function() {
        // Use the (possibly refreshed) token for this request.
        requestOptions.headers["Authorization"] = "Bearer " + JSON.parse(localStorage.getItem('user')).data.accessToken;
        return fetch(fullUrl, requestOptions);
    })
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;
                switch(response.status)
                {
                    case 401:
                        {
                            errorMsg = "The token is not valid.";

                            store.dispatch('users/logout', null, { root: true });
                            break;
                        }
                    default:
                        {
                            errorMsg = data;
                            break;
                        }
                }

                return Promise.reject(errorMsg);
            }

            return data;
        });
    })
    .catch((err) =>
    {
        if (err.name === 'AbortError')
        {
            console.log("The request was aborted!");
        }
        else
        {
            return Promise.reject(apiErrorMessage(err));
        }
    });
}

/* Handles 1) request cancellation 2) fetch url 3) error handler */
function apiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        fullUrl += "?";
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            if(value || value == 0)
            {
                var encodedValue = encodeURIComponent(value);

                fullUrl += `&${key}=${encodedValue}`;
            }
        }
    }

    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;
                switch(response.status)
                {
                    case 401:
                        {
                            errorMsg = "The token is not valid.";

                            store.dispatch('users/logout', null, { root: true });
                            break;
                        }
                    default:
                        {
                            errorMsg = data;
                            break;
                        }
                }

                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            console.log(err);
            return Promise.reject(apiErrorMessage(err));
        }
    });
}